/**
 * Edits an inline-conditional chip: its condition field, if/unless, and both branches as template
 * text. Opened by clicking a chip, or Enter on a selected one (cdm/conditionalRunEdit.ts); anchored
 * to the chip like PlaceholderPopover. Apply writes one transaction; Esc, Cancel or a click outside
 * discards.
 */
import { useCallback, useEffect, useLayoutEffect, useRef, useState, type CSSProperties, type FormEvent } from 'react'
import type { Editor } from '@tiptap/core'
import { colors, fontSize, radius, shadow, spacing } from '../../styles/theme'
import { placeholderNameError } from '../../cdm/placeholderEdit'
import { applyConditionalRunEdit, branchTextError, branchToText, setConditionalRunEditHandler } from '../../cdm/conditionalRunEdit'
import type { ConditionalRun } from '../../cdm/types'

const POPOVER_WIDTH = 360

interface Draft {
  rawName: string
  negate: boolean
  /** null: the branch holds content text cannot express, so it is shown read-only. */
  text: string | null
  hasElse: boolean
  elseText: string | null
}

function toDraft(run: ConditionalRun): Draft {
  return {
    rawName: run.rawName,
    negate: run.negate,
    text: branchToText(run.inlines),
    hasElse: run.elseInlines !== null,
    elseText: run.elseInlines ? branchToText(run.elseInlines) : '',
  }
}

export default function ConditionalRunPopover({ editor }: { editor: Editor | null }) {
  const [pos, setPos] = useState<number | null>(null)
  const [draft, setDraft] = useState<Draft | null>(null)
  const [anchor, setAnchor] = useState<{ top: number; left: number } | null>(null)
  const boxRef = useRef<HTMLFormElement>(null)
  const nameRef = useRef<HTMLInputElement>(null)

  const close = useCallback((refocus: boolean) => {
    setPos(null)
    setDraft(null)
    setAnchor(null)
    if (refocus) editor?.view.focus()
  }, [editor])

  useEffect(() => {
    if (!editor) return
    setConditionalRunEditHandler(editor, p => {
      const node = editor.state.doc.nodeAt(p)
      if (!node || node.type.name !== 'conditionalRun' || !node.attrs.json) return
      setDraft(toDraft(node.attrs.json as ConditionalRun))
      setPos(p)
    })
    return () => setConditionalRunEditHandler(editor, null)
  }, [editor])

  const reposition = useCallback(() => {
    if (!editor || pos === null) return
    const dom = editor.view.nodeDOM(pos)
    if (!(dom instanceof HTMLElement)) return
    const r = dom.getBoundingClientRect()
    const left = Math.max(8, Math.min(r.left, window.innerWidth - POPOVER_WIDTH - 8))
    const h = boxRef.current?.offsetHeight ?? 0
    const below = r.bottom + 6
    const top = below + h > window.innerHeight - 8 && r.top - 6 - h >= 8 ? r.top - 6 - h : below
    setAnchor({ top, left })
  }, [editor, pos])

  useLayoutEffect(() => {
    if (pos === null) return
    reposition()
    const raf = window.requestAnimationFrame(reposition)
    window.addEventListener('scroll', reposition, true)
    window.addEventListener('resize', reposition)
    return () => {
      window.cancelAnimationFrame(raf)
      window.removeEventListener('scroll', reposition, true)
      window.removeEventListener('resize', reposition)
    }
  }, [pos, reposition])

  useLayoutEffect(() => { reposition() }, [draft?.hasElse, reposition])

  useEffect(() => {
    if (pos === null) return
    nameRef.current?.focus()
    nameRef.current?.select()
    const onDown = (e: MouseEvent) => { if (!boxRef.current?.contains(e.target as Node)) close(false) }
    document.addEventListener('mousedown', onDown)
    return () => document.removeEventListener('mousedown', onDown)
  }, [pos, close])

  useEffect(() => {
    if (!editor || pos === null) return
    const check = () => { if (editor.state.doc.nodeAt(pos)?.type.name !== 'conditionalRun') close(false) }
    editor.on('update', check)
    return () => { editor.off('update', check) }
  }, [editor, pos, close])

  if (!editor || pos === null || !draft) return null

  const nameError = placeholderNameError(draft.rawName)
  const textError = draft.text !== null ? branchTextError(draft.text) : null
  const elseError = draft.hasElse && draft.elseText !== null ? branchTextError(draft.elseText) : null
  const invalid = nameError !== null || textError !== null || elseError !== null
  const set = (patch: Partial<Draft>) => setDraft(d => (d ? { ...d, ...patch } : d))

  function submit(e: FormEvent) {
    e.preventDefault()
    if (invalid || pos === null || !editor || !draft) return
    applyConditionalRunEdit(editor, pos, draft)
    close(true)
  }

  const input: CSSProperties = { padding: '4px 8px', border: `1px solid ${colors.border}`, borderRadius: radius.sm, fontSize: fontSize.sm, minWidth: 0, boxSizing: 'border-box' }
  const area: CSSProperties = { ...input, fontFamily: 'monospace', resize: 'vertical', minHeight: 48 }
  const label: CSSProperties = { display: 'flex', flexDirection: 'column', gap: 4, color: colors.textSecondary }
  const readOnly = (
    <div style={{ padding: '4px 8px', background: colors.background, borderRadius: radius.sm, color: colors.textSecondary, fontSize: fontSize.xs }}>
      Holds content that can only be changed in the template source (a nested condition, link or page field). Kept as it is.
    </div>
  )
  const error = (msg: string | null) => msg && <div role="alert" style={{ color: colors.error, fontSize: fontSize.xs }}>{msg}</div>

  return (
    <form
      ref={boxRef}
      role="dialog"
      aria-label="Edit inline condition"
      onSubmit={submit}
      onKeyDown={e => { if (e.key === 'Escape') { e.preventDefault(); e.stopPropagation(); close(true) } }}
      style={{
        position: 'fixed', top: anchor?.top ?? 0, left: anchor?.left ?? 0, width: POPOVER_WIDTH, zIndex: 30,
        visibility: anchor ? 'visible' : 'hidden', padding: spacing[3], background: colors.surface,
        border: `1px solid ${colors.border}`, borderRadius: radius.md, boxShadow: shadow.lg, fontSize: fontSize.sm, color: colors.text,
        display: 'flex', flexDirection: 'column', gap: spacing.sm, boxSizing: 'border-box',
      }}
    >
      <div style={{ display: 'flex', gap: spacing.sm, alignItems: 'flex-end' }}>
        <label style={{ ...label, flex: '0 0 auto' }}>
          Show when
          <select aria-label="Test" value={draft.negate ? 'unless' : 'if'} onChange={e => set({ negate: e.target.value === 'unless' })} style={input}>
            <option value="if">if</option>
            <option value="unless">unless</option>
          </select>
        </label>
        <label style={{ ...label, flex: 1 }}>
          Condition field
          <input
            ref={nameRef}
            value={draft.rawName}
            onChange={e => set({ rawName: e.target.value })}
            spellCheck={false}
            aria-invalid={nameError !== null}
            style={{ ...input, fontFamily: 'monospace' }}
          />
        </label>
      </div>
      {draft.rawName !== '' && error(nameError)}

      <label style={label}>
        {draft.negate ? 'Text when the field is empty or false' : 'Text when the field is set'}
        {draft.text === null ? readOnly : (
          <textarea aria-label="Branch text" value={draft.text} onChange={e => set({ text: e.target.value })} spellCheck={false} rows={2} style={area} />
        )}
      </label>
      {error(textError)}

      <label style={{ display: 'flex', alignItems: 'center', gap: 6, color: colors.textSecondary }}>
        <input type="checkbox" checked={draft.hasElse} onChange={e => set({ hasElse: e.target.checked })} />
        Otherwise show
      </label>
      {draft.hasElse && (draft.elseText === null ? readOnly : (
        <textarea aria-label="Else text" value={draft.elseText} onChange={e => set({ elseText: e.target.value })} spellCheck={false} rows={2} style={area} />
      ))}
      {error(elseError)}

      <div style={{ color: colors.textMuted, fontSize: fontSize.xs }}>Type fields as {'{{name}}'} or {'{{amount | currency:USD}}'}.</div>

      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: spacing.sm }}>
        <button type="button" onClick={() => close(true)} style={{ padding: '4px 12px', border: `1px solid ${colors.border}`, borderRadius: radius.sm, background: 'none', color: colors.textSecondary, cursor: 'pointer', fontSize: fontSize.sm }}>Cancel</button>
        <button type="submit" disabled={invalid} style={{ padding: '4px 12px', border: 'none', borderRadius: radius.sm, background: invalid ? colors.border : colors.primary, color: '#fff', cursor: invalid ? 'default' : 'pointer', fontSize: fontSize.sm, fontWeight: 600 }}>Apply</button>
      </div>
    </form>
  )
}
